/**
 * Entity search transport: fan-out to registered section providers in parallel.
 */

import { rankHitsByAcronym } from '@/shared/layout/spotlight/rankHitsByAcronym';
import { getEnabledSearchProviders } from './registry';
import type {
  EntitySearchTransport,
  GlobalSearchSectionResult,
} from './types';

const isAbortError = (err: unknown): boolean => {
  const e = err as { name?: string; code?: string };
  return (
    e?.name === 'CanceledError' ||
    e?.name === 'AbortError' ||
    e?.code === 'ERR_CANCELED'
  );
};

export const createFanOutProviderTransport = (): EntitySearchTransport => ({
  search: async ({ query, limit, signal, hasPermission }) => {
    const enabled = getEnabledSearchProviders(hasPermission);

    const fetchSection = async (
      provider: (typeof enabled)[number],
    ): Promise<GlobalSearchSectionResult> => {
      try {
        let hits = await provider.search(query, { limit, signal });
        hits = hits.slice(0, limit);
        if (provider.rankByAcronym) {
          hits = rankHitsByAcronym(hits, query);
        }
        return {
          id: provider.id,
          loading: false,
          error: false,
          hits,
        };
      } catch (err) {
        if (signal?.aborted || isAbortError(err)) {
          return {
            id: provider.id,
            loading: false,
            error: false,
            hits: [],
          };
        }
        return {
          id: provider.id,
          loading: false,
          error: true,
          hits: [],
        };
      }
    };

    return Promise.all(enabled.map(fetchSection));
  },
});
